import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Button, Center, Group, Loader, Modal, NumberInput, Select, SimpleGrid, Stack, Table, Text, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, offersApi, type OfferReference } from '../../api';
import { useContextMenu } from '../../ui/ContextMenu';
import { Menu } from '@mantine/core';

type RefInput = Omit<OfferReference, 'id'>;
const EMPTY: RefInput = { partyId: null, title: '', client: '', service: '', period: '', figures: '', category: '', note: '', sortOrder: 0 };

/**
 * The reference projects of the office and of its subcontractors (AP-102),
 * kept once, chosen per offer for the reference list.
 */
export function ReferencesPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['offer-references'], queryFn: offersApi.references });
  const parties = useQuery({ queryKey: ['offer-parties'], queryFn: offersApi.parties });
  const [editing, setEditing] = useState<OfferReference | 'new' | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => offersApi.removeReference(id), onSettled: () => queryClient.invalidateQueries({ queryKey: ['offer-references'] }) });
  const byId = new Map((parties.data ?? []).map((p) => [p.id, p.name]));
  const menu = useContextMenu<OfferReference>((r, close) => (
    <>
      <Menu.Label>{r.title.slice(0, 40)}</Menu.Label>
      <Menu.Item onClick={() => { close(); setEditing(r); }}>Bearbeiten …</Menu.Item>
      <Menu.Item onClick={() => { close(); setEditing({ ...r, id: '', title: `${r.title} (Kopie)` }); }}>Als Kopie anlegen …</Menu.Item>
      <Menu.Item color="red" onClick={() => { close(); if (window.confirm(`„${r.title}“ löschen?`)) remove.mutate(r.id); }}>Löschen …</Menu.Item>
    </>
  ));
  if (list.isPending) return <Center h={120}><Loader /></Center>;
  if (list.isError) return <Alert color="red">{list.error instanceof ApiError ? list.error.message : 'Die Referenzen konnten nicht geladen werden.'}</Alert>;
  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">Referenzprojekte des Büros und der Nachunternehmer, einmal gepflegt und je Angebot gewählt (Angaben und Texte › Anlagen im Paket). Die Liste im Angebot gliedert sich nach Beteiligten und Gruppe.</Text>
      <Group><Button size="xs" onClick={() => setEditing('new')}>Referenz anlegen</Button></Group>
      {list.data.length === 0 ? <Text size="sm" c="dimmed">Noch keine Referenzen.</Text> : (
        <Table.ScrollContainer minWidth={800}>
          <Table verticalSpacing={4} highlightOnHover>
            <Table.Thead><Table.Tr><Table.Th w={30} /><Table.Th>Projekt · Ort</Table.Th><Table.Th>Bauherr / Auslober</Table.Th><Table.Th>Leistung</Table.Th><Table.Th w={110}>Zeitraum</Table.Th><Table.Th w={160}>Gruppe</Table.Th><Table.Th w={140}>von</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {list.data.map((r) => (
                <Table.Tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setEditing(r)} onContextMenu={(e) => menu.open(e, r)}>
                  <Table.Td><ActionIcon size="xs" variant="subtle" color="gray" aria-label="Löschen" title="Löschen" onClick={(e) => { e.stopPropagation(); if (window.confirm(`„${r.title}“ löschen?`)) remove.mutate(r.id); }}>✕</ActionIcon></Table.Td>
                  <Table.Td><Text size="sm" fw={500}>{r.title}</Text></Table.Td>
                  <Table.Td><Text size="xs">{r.client}</Text></Table.Td>
                  <Table.Td><Text size="xs" lineClamp={2}>{r.service}</Text></Table.Td>
                  <Table.Td><Text size="xs">{r.period}</Text></Table.Td>
                  <Table.Td><Text size="xs">{r.category}</Text></Table.Td>
                  <Table.Td><Text size="xs">{r.partyId ? byId.get(r.partyId) ?? '' : 'Büro'}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {menu.menu}
      {editing && <ReferenceModal reference={editing === 'new' ? null : editing} parties={(parties.data ?? []).map((p) => ({ value: p.id, label: p.name }))} onClose={() => setEditing(null)} />}
    </Stack>
  );
}

function ReferenceModal({ reference, parties, onClose }: { reference: OfferReference | null; parties: { value: string; label: string }[]; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RefInput>(() => (reference ? (({ id: _, ...rest }) => rest)(reference) : EMPTY));
  const save = useMutation({
    mutationFn: () => (reference?.id ? offersApi.saveReference(reference.id, form) : offersApi.addReference(form).then(() => undefined)),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['offer-references'] }); onClose(); },
  });
  const field = (key: 'title' | 'client' | 'service' | 'period' | 'figures' | 'category', label: string, placeholder?: string): JSX.Element => (
    <TextInput size="xs" label={label} placeholder={placeholder} value={form[key]} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, [key]: v })); }} />
  );
  return (
    <Modal opened onClose={onClose} title={reference?.id ? 'Referenz bearbeiten' : 'Referenz anlegen'} size="lg">
      <Stack gap="xs">
        {field('title', 'Projekt · Ort', 'Kita Sonnenwinkel, Musterstadt')}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          {field('client', 'Bauherr / Auslober')}
          {field('period', 'Zeitraum', '01/2022 – 05/2024')}
          {field('service', 'Leistung', 'Neubau, LP 1–9; Projektleitung')}
          {field('figures', 'Kenndaten', 'BGF 850 m², Baukosten KG 300+400 …')}
          {field('category', 'Gruppe in der Liste', 'z. B. Hochbau und Genehmigung')}
          <Select size="xs" label="von" data={[{ value: '', label: 'Büro' }, ...parties]} value={form.partyId ?? ''} onChange={(v) => setForm((f) => ({ ...f, partyId: v || null }))} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
        </SimpleGrid>
        <Textarea size="xs" label="Hinweis unter der Gruppe (optional)" autosize minRows={2} value={form.note} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, note: v })); }} />
        <NumberInput size="xs" label="Reihenfolge" w={120} value={form.sortOrder} min={0} onChange={(v) => setForm((f) => ({ ...f, sortOrder: Number(v) || 0 }))} />
        {save.isError && <Alert color="red" py={6}>{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!form.title.trim()}>Speichern</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
